import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import { isBaseSchema } from '../../utils/schemaLifecycle';
import styles from './AssignFridgePicker.module.css';

const MODES = [
  { key: 'search', label: 'Search' },
  { key: 'paste', label: 'Paste IDs' },
  { key: 'upload', label: 'Upload CSV' },
  { key: 'all', label: 'Assign to all active' },
];

// One entry per filterable location attribute. Adding a new filter only requires
// adding to this array + the roster normalizer in SleepScreenAssign.js.
const FILTERS = [
  { key: 'region', label: 'Region' },
  { key: 'market', label: 'Market' },
  { key: 'vertical', label: 'Vertical' },
  { key: 'cluster', label: 'Cluster' },
];

const SEARCH_PAGE_SIZE = 100;

const parseIdsFromText = (text) =>
  text
    .split(/[\n,]+/)
    .map((s) => s.trim())
    .filter(Boolean);

const AssignFridgePicker = ({
  roster,
  rosterLoading,
  currentAssignments,
  selection,
  onSelectionChange,
}) => {
  const [mode, setMode] = useState('search');
  const [searchTerm, setSearchTerm] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const searchInputRef = useRef(null);
  const [activeFilters, setActiveFilters] = useState({});
  const [openFilterKey, setOpenFilterKey] = useState(null);
  const [hideCurrent, setHideCurrent] = useState(false);
  const [visibleLimit, setVisibleLimit] = useState(SEARCH_PAGE_SIZE);
  const [pasteText, setPasteText] = useState('');
  const [pasteResult, setPasteResult] = useState(null);
  const [uploadResult, setUploadResult] = useState(null);
  const fileInputRef = useRef(null);
  // Preserve scroll position across re-renders when lockedSet changes
  // reorder the filtered list (e.g., currentAssignments fetch lands).
  const tableWrapperRef = useRef(null);
  const savedScrollTop = useRef(0);
  useLayoutEffect(() => {
    if (tableWrapperRef.current) {
      tableWrapperRef.current.scrollTop = savedScrollTop.current;
    }
  });

  const lockedSet = useMemo(
    () => new Set(currentAssignments),
    [currentAssignments],
  );

  const filterOptions = useMemo(() => {
    const out = {};
    FILTERS.forEach(({ key }) => {
      const set = new Set();
      roster.forEach((f) => {
        if (f[key]) set.add(f[key]);
      });
      out[key] = Array.from(set).sort((a, b) => a.localeCompare(b));
    });
    return out;
  }, [roster]);

  const activeFilterCount = Object.values(activeFilters).filter(Boolean).length;

  const rosterAfterFilters = useMemo(() => {
    if (activeFilterCount === 0) return roster;
    return roster.filter((f) =>
      FILTERS.every(({ key }) => {
        const value = activeFilters[key];
        return !value || f[key] === value;
      }),
    );
  }, [roster, activeFilters, activeFilterCount]);

  const setFilterValue = (key, value) => {
    setActiveFilters((prev) => ({ ...prev, [key]: value }));
    setVisibleLimit(SEARCH_PAGE_SIZE);
    setOpenFilterKey(null);
  };

  const clearAllFilters = () => {
    setActiveFilters({});
    setVisibleLimit(SEARCH_PAGE_SIZE);
    setOpenFilterKey(null);
  };

  const activeSchemaIds = useMemo(
    () => rosterAfterFilters.filter((f) => f.isActive).map((f) => f.schemaId),
    [rosterAfterFilters],
  );

  const toggleSelection = (schemaId) => {
    if (lockedSet.has(schemaId)) return;
    const next = new Set(selection);
    if (next.has(schemaId)) next.delete(schemaId);
    else next.add(schemaId);
    onSelectionChange(next);
  };

  const addManyToSelection = (schemaIds) => {
    const next = new Set(selection);
    schemaIds.forEach((id) => {
      if (!lockedSet.has(id)) next.add(id);
    });
    onSelectionChange(next);
  };

  // Locked (already-current) fridges are intentionally excluded from the
  // selection Set — the UI's isChecked render reads `isLocked || selection.has`
  // to display them as checked, but they shouldn't be re-sent to the BE.
  const replaceSelection = (schemaIds) => {
    const next = new Set();
    schemaIds.forEach((id) => {
      if (!lockedSet.has(id)) next.add(id);
    });
    onSelectionChange(next);
  };

  const filteredRoster = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    const matched = term
      ? rosterAfterFilters.filter((f) =>
          (f.schemaId || '').toLowerCase().includes(term),
        )
      : rosterAfterFilters;
    // When "Hide already current" is on, drop locked rows entirely so each
    // Load-more page is 100 actionable rows instead of 100 locked rows.
    if (hideCurrent) {
      return matched.filter((f) => !lockedSet.has(f.schemaId));
    }
    // Otherwise pin Current (locked) rows to the top so Marketing sees what's
    // already assigned without scrolling. Within each group, preserve order.
    const lockedRows = [];
    const otherRows = [];
    matched.forEach((f) => {
      if (lockedSet.has(f.schemaId)) lockedRows.push(f);
      else otherRows.push(f);
    });
    return [...lockedRows, ...otherRows];
  }, [rosterAfterFilters, searchTerm, lockedSet, hideCurrent]);

  // Count locked rows against the pre-hide list so the "N already current"
  // copy stays stable when the user toggles Hide on/off.
  const currentCount = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    const source = term
      ? rosterAfterFilters.filter((f) =>
          (f.schemaId || '').toLowerCase().includes(term),
        )
      : rosterAfterFilters;
    return source.filter((f) => lockedSet.has(f.schemaId)).length;
  }, [rosterAfterFilters, searchTerm, lockedSet]);

  const selectableIds = useMemo(
    () =>
      filteredRoster
        .filter((f) => !lockedSet.has(f.schemaId))
        .map((f) => f.schemaId),
    [filteredRoster, lockedSet],
  );

  // Drives the toggle button: true when every visible selectable id is in
  // selection (Marketing has at least picked the visible set).
  const allSelectableSelected =
    selectableIds.length > 0 && selectableIds.every((id) => selection.has(id));

  const rosterIdSet = useMemo(() => {
    const set = new Set();
    roster.forEach((f) => set.add(f.schemaId));
    return set;
  }, [roster]);

  // Selection may contain ids that aren't in the roster (e.g., pasted IDs the
  // BE will validate). Count them so the toggle copy reflects total selection.
  const offRosterSelectedCount = useMemo(() => {
    let count = 0;
    selection.forEach((id) => {
      if (!lockedSet.has(id) && !rosterIdSet.has(id)) count += 1;
    });
    return count;
  }, [selection, lockedSet, rosterIdSet]);

  const handleToggleAllSelectable = () => {
    if (allSelectableSelected) {
      // Clear ALL non-locked selection, including off-roster pasted IDs, so
      // the user gets the clean slate the button label promises.
      onSelectionChange(new Set());
    } else {
      addManyToSelection(selectableIds);
    }
  };

  const visibleRoster = filteredRoster.slice(0, visibleLimit);
  const hasMore = filteredRoster.length > visibleLimit;

  const dedupeIds = (ids) => {
    const seen = new Set();
    const out = [];
    ids.forEach((id) => {
      if (seen.has(id)) return;
      seen.add(id);
      out.push(id);
    });
    return out;
  };

  // Split pasted/uploaded ids three ways: active fridges, base/master schemas
  // (real rows, the BE accepts them — see the drift note), and genuinely
  // unrecognized ids (typos/dead ids that will error on assign).
  const categorizeIds = (ids) => {
    let matched = 0;
    let baseCount = 0;
    const unrecognized = [];
    ids.forEach((id) => {
      if (rosterIdSet.has(id)) matched += 1;
      else if (isBaseSchema(id)) baseCount += 1;
      else unrecognized.push(id);
    });
    return { matched, baseCount, unrecognized };
  };

  const handlePasteApply = () => {
    const ids = dedupeIds(parseIdsFromText(pasteText));
    if (ids.length === 0) {
      setPasteResult(null);
      return;
    }
    setPasteResult(categorizeIds(ids));
    addManyToSelection(ids);
  };

  const handleFileUpload = (event) => {
    const file = event.target.files && event.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        // Strip UTF-8 BOM (Excel-saved CSVs often include one).
        const text = String(e.target.result || '').replace(/^\uFEFF/, '');
        const lines = text
          .split(/\r?\n/)
          .map((l) => l.trim())
          .filter(Boolean);
        if (lines.length === 0) {
          toast.error('CSV is empty.');
          setUploadResult(null);
          return;
        }
        // Per-cell: trim + strip a single pair of surrounding double-quotes.
        const splitLine = (line) =>
          line.split(',').map((cell) => cell.trim().replace(/^"(.*)"$/, '$1'));
        // The CSV must look like a fridge-assignment file. We require an ID
        // column AND we reject any header outside this allowlist so files like
        // the item_prices export (which also has a `schemaId` column but is
        // really a pricing file) don't get misread as fridges.
        const ID_HEADERS = new Set([
          'schemaid',
          'fridge_id',
          'fridge id',
          'fridge_name',
          'id',
        ]);
        const ALLOWED_HEADERS = new Set([...ID_HEADERS, 'slot']);
        const headerCells = splitLine(lines[0]).map((c) => c.toLowerCase());
        const idColumnIndex = headerCells.findIndex((c) => ID_HEADERS.has(c));
        if (idColumnIndex === -1) {
          toast.error(
            "CSV needs a column named 'schemaId', 'fridge_id', or 'fridge_name'. Check the file's first row.",
          );
          setUploadResult(null);
          return;
        }
        const unexpectedHeaders = headerCells.filter(
          (c) => c && !ALLOWED_HEADERS.has(c),
        );
        if (unexpectedHeaders.length > 0) {
          toast.error(
            `This doesn't look like a fridge assignment CSV. Unexpected columns: ${unexpectedHeaders
              .slice(0, 4)
              .join(', ')}${unexpectedHeaders.length > 4 ? ', …' : ''}.`,
          );
          setUploadResult(null);
          return;
        }
        const ids = dedupeIds(
          lines
            .slice(1)
            .map((line) => splitLine(line)[idColumnIndex])
            .filter(Boolean),
        );
        if (ids.length === 0) {
          toast.error('CSV had a header row but no data rows below it.');
          setUploadResult(null);
          return;
        }
        setUploadResult({ filename: file.name, ...categorizeIds(ids) });
        addManyToSelection(ids);
      } catch (err) {
        console.error('CSV parse failed:', err);
        toast.error(
          'Could not parse CSV file. Expected one fridge ID per line.',
        );
        setUploadResult(null);
      }
    };
    reader.onerror = () => {
      toast.error('Could not read CSV file.');
    };
    reader.readAsText(file);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleAssignToAll = () => {
    replaceSelection(activeSchemaIds);
  };

  if (rosterLoading) {
    return <div className={styles.stateBlock}>Loading fleet roster…</div>;
  }

  return (
    <div className={styles.picker}>
      <div className={styles.tabs} role="tablist">
        {MODES.map((m) => (
          <button
            key={m.key}
            type="button"
            role="tab"
            aria-selected={mode === m.key}
            className={mode === m.key ? styles.tabActive : styles.tab}
            onClick={() => {
              setMode(m.key);
              setOpenFilterKey(null);
            }}
          >
            {m.label}
          </button>
        ))}
      </div>

      {mode === 'search' && (
        <div className={styles.pane}>
          <div className={styles.searchRow}>
            {searchOpen || searchTerm ? (
              <input
                ref={searchInputRef}
                type="search"
                className={styles.searchInput}
                placeholder="search fleet by fridge id…"
                aria-label="Search fridges"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setVisibleLimit(SEARCH_PAGE_SIZE);
                }}
                onBlur={() => {
                  if (!searchTerm) setSearchOpen(false);
                }}
              />
            ) : (
              <button
                type="button"
                className={styles.searchToggle}
                aria-label="Open search"
                onClick={() => {
                  setSearchOpen(true);
                  setTimeout(() => {
                    if (searchInputRef.current) searchInputRef.current.focus();
                  }, 0);
                }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="18"
                  height="18"
                  viewBox="0 0 16 16"
                  fill="currentColor"
                  aria-hidden="true"
                >
                  <path d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001c.03.04.062.078.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1.007 1.007 0 0 0-.115-.1zM12 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0z" />
                </svg>
              </button>
            )}
            {FILTERS.filter((f) => filterOptions[f.key].length > 0).map(
              ({ key, label }) => {
                const value = activeFilters[key];
                const isOpen = openFilterKey === key;
                let chipClass = styles.filterChip;
                if (value) chipClass = styles.filterChipActive;
                if (isOpen) chipClass = styles.filterChipOpen;
                return (
                  <button
                    key={key}
                    type="button"
                    className={chipClass}
                    onClick={() => setOpenFilterKey(isOpen ? null : key)}
                    aria-expanded={isOpen}
                  >
                    <span className={styles.filterChipLabel}>{label}</span>
                    {value && (
                      <>
                        <span className={styles.filterChipValue}>{value}</span>
                        <span
                          className={styles.filterChipClear}
                          role="button"
                          tabIndex={0}
                          aria-label={`Clear ${label}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            setFilterValue(key, '');
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              e.stopPropagation();
                              setFilterValue(key, '');
                            }
                          }}
                        >
                          ×
                        </span>
                      </>
                    )}
                    {!value && (
                      <span className={styles.filterChipCaret}>
                        {isOpen ? '▴' : '▾'}
                      </span>
                    )}
                  </button>
                );
              },
            )}
            {activeFilterCount > 0 && (
              <button
                type="button"
                className={styles.clearFilterButton}
                onClick={clearAllFilters}
              >
                clear all
              </button>
            )}
          </div>
          {openFilterKey && (
            <div className={styles.filterPopover}>
              <button
                type="button"
                className={styles.filterPopoverOption}
                onClick={() => setFilterValue(openFilterKey, '')}
              >
                Any
              </button>
              {filterOptions[openFilterKey].map((v) => {
                const isSelected = activeFilters[openFilterKey] === v;
                return (
                  <button
                    key={v}
                    type="button"
                    className={
                      isSelected
                        ? styles.filterPopoverOptionActive
                        : styles.filterPopoverOption
                    }
                    onClick={() => setFilterValue(openFilterKey, v)}
                  >
                    {v}
                  </button>
                );
              })}
            </div>
          )}
          <div className={styles.resultCount}>
            <span className={styles.resultCountLeft}>
              <span>
                {filteredRoster.length} of {roster.length} fridges
                {activeFilterCount > 0 &&
                  ` · ${activeFilterCount} ${
                    activeFilterCount === 1 ? 'filter' : 'filters'
                  } active`}
              </span>
              {selectableIds.length > 0 && (
                <button
                  type="button"
                  className={styles.selectAllButton}
                  onClick={handleToggleAllSelectable}
                >
                  {allSelectableSelected
                    ? `clear selection (${
                        selectableIds.length + offRosterSelectedCount
                      })`
                    : `select all ${selectableIds.length}`}
                </button>
              )}
            </span>
            {(currentCount > 0 || hideCurrent) && (
              <label
                htmlFor="hide-current-toggle"
                className={styles.hideCurrentToggle}
              >
                <input
                  id="hide-current-toggle"
                  type="checkbox"
                  checked={hideCurrent}
                  onChange={(e) => {
                    setHideCurrent(e.target.checked);
                    setVisibleLimit(SEARCH_PAGE_SIZE);
                  }}
                />
                {hideCurrent ? 'Hiding' : 'Hide'} {currentCount} already current
              </label>
            )}
          </div>
          <div
            className={styles.tableWrapper}
            ref={tableWrapperRef}
            onScroll={(e) => {
              savedScrollTop.current = e.currentTarget.scrollTop;
            }}
          >
            <table className={styles.table}>
              <thead>
                <tr>
                  <th className={styles.checkCol} aria-label="Select" />
                  <th>Fridge ID</th>
                </tr>
              </thead>
              <tbody>
                {visibleRoster.map((fridge, index) => {
                  const isLocked = lockedSet.has(fridge.schemaId);
                  const isChecked = isLocked || selection.has(fridge.schemaId);
                  const prev = index > 0 ? visibleRoster[index - 1] : null;
                  const prevLocked = prev
                    ? lockedSet.has(prev.schemaId)
                    : false;
                  const showDivider = prev && prevLocked && !isLocked;
                  return (
                    <React.Fragment key={fridge.schemaId}>
                      {showDivider && (
                        <tr className={styles.dividerRow} aria-hidden="true">
                          <td colSpan={2} className={styles.dividerCell}>
                            <span>Other fridges</span>
                          </td>
                        </tr>
                      )}
                      <tr className={isLocked ? styles.lockedRow : undefined}>
                        <td className={styles.checkCol}>
                          <input
                            type="checkbox"
                            checked={isChecked}
                            disabled={isLocked}
                            onChange={() => toggleSelection(fridge.schemaId)}
                            aria-label={`Select ${fridge.schemaId}`}
                          />
                        </td>
                        <td className={styles.fridgeIdCell}>
                          <span className={styles.mono}>{fridge.schemaId}</span>
                          {isLocked && (
                            <span className={styles.lockedTag}>Current</span>
                          )}
                        </td>
                      </tr>
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
          {hasMore && (
            <div className={styles.loadMoreRow}>
              <span className={styles.loadMoreCount}>
                Showing {visibleRoster.length} of {filteredRoster.length}
              </span>
              <button
                type="button"
                className={styles.loadMoreButton}
                onClick={() => setVisibleLimit((n) => n + SEARCH_PAGE_SIZE)}
              >
                Load{' '}
                {Math.min(
                  SEARCH_PAGE_SIZE,
                  filteredRoster.length - visibleLimit,
                )}{' '}
                more
              </button>
            </div>
          )}
        </div>
      )}

      {mode === 'paste' && (
        <div className={styles.pane}>
          <p className={styles.helper}>
            Paste fridge IDs — one per line, or comma-separated.
          </p>
          <textarea
            className={styles.textarea}
            placeholder="MD_CHI_AIRPORT_01&#10;MD_CHI_AIRPORT_02&#10;..."
            value={pasteText}
            onChange={(e) => {
              setPasteText(e.target.value);
              setPasteResult(null);
            }}
            rows={10}
          />
          <div className={styles.paneActions}>
            <button
              type="button"
              className={styles.primaryButton}
              onClick={handlePasteApply}
              disabled={!pasteText.trim()}
            >
              Match against fleet
            </button>
            <button
              type="button"
              className={styles.secondaryButton}
              onClick={() => {
                setPasteText('');
                setPasteResult(null);
              }}
            >
              Clear
            </button>
          </div>
          {pasteResult && (
            <div className={styles.matchSummary}>
              <strong>{pasteResult.matched}</strong> matched the active fleet
              (live, or launching and not yet open)
              {pasteResult.baseCount > 0 && (
                <>
                  {' · '}
                  <strong>{pasteResult.baseCount}</strong>{' '}
                  {pasteResult.baseCount === 1 ? 'base schema' : 'base schemas'}
                </>
              )}
              {pasteResult.unrecognized.length > 0 && (
                <>
                  {' · '}
                  <span className={styles.matchSummaryWarn}>
                    <strong>{pasteResult.unrecognized.length}</strong> not
                    recognized
                  </span>
                </>
              )}
              . All added to selection.
              {pasteResult.unrecognized.length > 0 && (
                <>
                  <div className={styles.matchSummaryWarn}>
                    Not recognized — these will error on the backend after you
                    assign:
                  </div>
                  <ul className={styles.notFoundList}>
                    {pasteResult.unrecognized.slice(0, 50).map((id) => (
                      <li key={id}>{id}</li>
                    ))}
                  </ul>
                </>
              )}
            </div>
          )}
        </div>
      )}

      {mode === 'upload' && (
        <div className={styles.pane}>
          <p className={styles.helper}>
            Upload a CSV with a header row. The first column with header{' '}
            <code>schemaId</code>, <code>fridge_id</code>, or{' '}
            <code>fridge_name</code> will be read as the fridge ID.
          </p>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,text/csv"
            onChange={handleFileUpload}
            className={styles.fileInput}
            aria-label="Upload CSV of fridge IDs"
          />
          {uploadResult && (
            <div className={styles.matchSummary}>
              <div>
                Imported <strong>{uploadResult.filename}</strong>
              </div>
              <strong>{uploadResult.matched}</strong> matched the active fleet
              (live, or launching and not yet open)
              {uploadResult.baseCount > 0 && (
                <>
                  {' · '}
                  <strong>{uploadResult.baseCount}</strong>{' '}
                  {uploadResult.baseCount === 1
                    ? 'base schema'
                    : 'base schemas'}
                </>
              )}
              {uploadResult.unrecognized.length > 0 && (
                <>
                  {' · '}
                  <span className={styles.matchSummaryWarn}>
                    <strong>{uploadResult.unrecognized.length}</strong> not
                    recognized
                  </span>
                </>
              )}
              . All added to selection.
              {uploadResult.unrecognized.length > 0 && (
                <>
                  <div className={styles.matchSummaryWarn}>
                    Not recognized — these will error on the backend after you
                    assign:
                  </div>
                  <ul className={styles.notFoundList}>
                    {uploadResult.unrecognized.slice(0, 50).map((id) => (
                      <li key={id}>{id}</li>
                    ))}
                  </ul>
                </>
              )}
            </div>
          )}
        </div>
      )}

      {mode === 'all' && (
        <div className={styles.pane}>
          <p className={styles.helper}>
            Replaces your current selection with every active fridge in the
            fleet (live, or launching and not yet open). Already-assigned
            fridges for this entry+slot stay locked and aren’t re-counted in the
            new total.
          </p>
          <button
            type="button"
            className={styles.primaryButton}
            onClick={handleAssignToAll}
            disabled={activeSchemaIds.length === 0}
          >
            Select all {activeSchemaIds.length} active fridges
          </button>
          <button
            type="button"
            className={styles.secondaryButton}
            onClick={() => onSelectionChange(new Set())}
          >
            Clear selection
          </button>
        </div>
      )}
    </div>
  );
};

AssignFridgePicker.propTypes = {
  roster: PropTypes.arrayOf(
    PropTypes.shape({
      schemaId: PropTypes.string.isRequired,
      isActive: PropTypes.bool,
      statusLabel: PropTypes.string,
      vertical: PropTypes.string,
      region: PropTypes.string,
      market: PropTypes.string,
      cluster: PropTypes.string,
    }),
  ).isRequired,
  rosterLoading: PropTypes.bool,
  currentAssignments: PropTypes.arrayOf(PropTypes.string),
  selection: PropTypes.instanceOf(Set).isRequired,
  onSelectionChange: PropTypes.func.isRequired,
};

AssignFridgePicker.defaultProps = {
  rosterLoading: false,
  currentAssignments: [],
};

export default AssignFridgePicker;
